import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CorpusItem,
  PolicyAcceptanceMatrix,
  PolicyRelease,
  PublishPolicyInput,
} from '@grc/types';
import { POLICY_DUE_DAYS_DEFAULT, versionLabel } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  Field,
  Input,
  Select,
  Textarea,
  formatDate,
  useToast,
} from '../../components/ui.js';
import { matrixKey, policiesKey, releasesKey } from './shared.js';
import './policies.css';

type Interval = 'none' | '12' | 'custom';

/**
 * Publish dialog: choose how the release is treated (minor vs material),
 * the acceptance window, optional periodic reconfirmation and whether to
 * email staff now. A first release always requires acceptance.
 */
function PublishPolicyDialog({
  item,
  live,
  open,
  onClose,
}: {
  item: CorpusItem;
  live: PolicyRelease | null;
  open: boolean;
  onClose: () => void;
}) {
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const firstRelease = live === null;

  const [releaseNotes, setReleaseNotes] = useState('');
  const [dueDays, setDueDays] = useState(String(POLICY_DUE_DAYS_DEFAULT));
  const [requiresAcceptance, setRequiresAcceptance] = useState(true);
  const [interval, setInterval] = useState<Interval>(
    live?.reacceptIntervalMonths === null || live === null ? 'none' : live.reacceptIntervalMonths === 12 ? '12' : 'custom',
  );
  const [customMonths, setCustomMonths] = useState(String(live?.reacceptIntervalMonths ?? 6));
  const [notify, setNotify] = useState(true);

  const effectiveRequires = firstRelease || requiresAcceptance;
  const months = interval === 'none' ? null : interval === '12' ? 12 : Number(customMonths);
  const current = versionLabel(item.documentVersion, item.currentVersion);

  const publish = useMutation({
    mutationFn: () =>
      apiFetch<PolicyRelease>(`/corpus/${item.id}/releases`, {
        method: 'POST',
        body: {
          requiresAcceptance: effectiveRequires,
          releaseNotes: releaseNotes.trim(),
          dueDays: Number(dueDays),
          reacceptIntervalMonths: months,
          notify: effectiveRequires && notify,
        } satisfies PublishPolicyInput,
      }),
    onSuccess: (release) => {
      toast(
        `${versionLabel(release.documentVersion, release.versionNumber)} published for acceptance${
          release.requiresAcceptance ? ' — staff must accept it' : ' — existing acceptances carry forward'
        }.`,
        'success',
      );
      void queryClient.invalidateQueries({ queryKey: policiesKey(org.orgId) });
      void queryClient.invalidateQueries({ queryKey: ['corpus-item', item.id] });
      void queryClient.invalidateQueries({ queryKey: ['corpus', org.orgId] });
      setReleaseNotes('');
      onClose();
    },
    onError: (err) => toast(err instanceof ApiRequestError ? err.message : 'Could not publish', 'error'),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    publish.mutate();
  };
  const dueValid = Number.isInteger(Number(dueDays)) && Number(dueDays) >= 1 && Number(dueDays) <= 90;
  const monthsValid = months === null || (Number.isInteger(months) && months >= 1 && months <= 36);

  return (
    <Dialog open={open} onClose={onClose} title={`Publish ${current} for acceptance`}>
      <div className="policy-dialog">
        <p className="policy-dialog-intro">
          Staff will read and accept exactly {current} of “{item.title}”. Later edits change nothing
          for them until you publish again.
        </p>
        <form onSubmit={submit}>
          <Field label="Release notes" hint="What changed — shown to staff as “What's changed”">
            {(props) => (
              <Textarea
                {...props}
                maxLength={2000}
                rows={3}
                value={releaseNotes}
                onChange={(e) => setReleaseNotes(e.target.value)}
              />
            )}
          </Field>
          <label className="policy-checkbox">
            <input
              type="checkbox"
              checked={effectiveRequires}
              disabled={firstRelease}
              onChange={(e) => setRequiresAcceptance(e.target.checked)}
            />
            <span>
              Staff must re-accept this version
              <span className="field-hint">
                {firstRelease
                  ? 'A first release always requires acceptance.'
                  : 'Leave off for typo or formatting fixes — existing acceptances carry forward.'}
              </span>
            </span>
          </label>
          <Field label="Days to accept" required hint="Counted from publication, or from the day a new starter joins">
            {(props) => (
              <Input
                {...props}
                type="number"
                min={1}
                max={90}
                required
                value={dueDays}
                onChange={(e) => setDueDays(e.target.value)}
              />
            )}
          </Field>
          <div className="policy-interval-row">
            <Field label="Periodic reconfirmation" hint="Ask staff to confirm again after this long">
              {(props) => (
                <Select {...props} value={interval} onChange={(e) => setInterval(e.target.value as Interval)}>
                  <option value="none">None — only on material change</option>
                  <option value="12">Every 12 months</option>
                  <option value="custom">Custom…</option>
                </Select>
              )}
            </Field>
            {interval === 'custom' && (
              <Field label="Months" required>
                {(props) => (
                  <Input
                    {...props}
                    type="number"
                    min={1}
                    max={36}
                    required
                    style={{ width: 90 }}
                    value={customMonths}
                    onChange={(e) => setCustomMonths(e.target.value)}
                  />
                )}
              </Field>
            )}
          </div>
          <label className="policy-checkbox">
            <input
              type="checkbox"
              checked={effectiveRequires && notify}
              disabled={!effectiveRequires}
              onChange={(e) => setNotify(e.target.checked)}
            />
            <span>
              Email staff now
              <span className="field-hint">
                {effectiveRequires
                  ? 'Everyone who must accept gets a link to this version.'
                  : 'Nothing to email — acceptances carry forward.'}
              </span>
            </span>
          </label>
          <div className="dialog-actions">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!dueValid || !monthsValid} loading={publish.isPending}>
              Publish {current}
            </Button>
          </div>
        </form>
      </div>
    </Dialog>
  );
}

/**
 * The "Acceptance" card on a corpus item: admins publish, withdraw and see
 * the release history; other members see a read-only line.
 */
export function PolicyAcceptanceCard({ item, isAdmin }: { item: CorpusItem; isAdmin: boolean }) {
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const archived = item.status === 'archived';

  const releases = useQuery({
    queryKey: releasesKey(org.orgId, item.id),
    queryFn: () => apiFetch<PolicyRelease[]>(`/corpus/${item.id}/releases`),
  });
  const matrix = useQuery({
    queryKey: matrixKey(org.orgId),
    queryFn: () => apiFetch<PolicyAcceptanceMatrix>(`/orgs/${org.orgId}/policy-acceptance`),
    enabled: isAdmin,
  });

  const [publishOpen, setPublishOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const withdraw = useMutation({
    mutationFn: () => apiFetch(`/corpus/${item.id}/releases/current`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Withdrawn from acceptance — existing acceptances are kept as evidence.', 'success');
      void queryClient.invalidateQueries({ queryKey: policiesKey(org.orgId) });
      setWithdrawOpen(false);
    },
    onError: (err) => toast(err instanceof ApiRequestError ? err.message : 'Could not withdraw', 'error'),
  });

  const list = releases.data ?? [];
  const live = list.find((r) => r.supersededAt === null) ?? null;
  const current = versionLabel(item.documentVersion, item.currentVersion);
  const liveLabel = live ? versionLabel(live.documentVersion, live.versionNumber) : '';

  if (!isAdmin) {
    if (!live) return null;
    return (
      <Card title="Acceptance">
        <p className="policy-card-line">
          Published for acceptance: {liveLabel} ({formatDate(live.publishedAt)}).{' '}
          <Link to={`/policies/${live.id}`}>Read and accept →</Link>
        </p>
      </Card>
    );
  }

  const accepted = matrix.data?.members.filter((m) =>
    m.policies.some((p) => p.corpusItemId === item.id && p.status === 'accepted'),
  ).length;
  const total = matrix.data?.members.length;

  return (
    <Card
      title="Acceptance"
      actions={
        !archived && item.currentVersion > 0 ? (
          <div className="policy-card-actions">
            {live && (
              <Button size="sm" variant="ghost" onClick={() => setWithdrawOpen(true)}>
                Withdraw
              </Button>
            )}
            <Button
              size="sm"
              variant={live ? 'secondary' : 'primary'}
              disabled={live?.versionNumber === item.currentVersion}
              onClick={() => setPublishOpen(true)}
            >
              {live ? `Publish ${current}` : 'Publish for acceptance'}
            </Button>
          </div>
        ) : undefined
      }
    >
      {live ? (
        <>
          <p className="policy-card-line">
            <span>
              Published {liveLabel} on {formatDate(live.publishedAt)}
              {total !== undefined && accepted !== undefined && (
                <>
                  {' '}
                  · {accepted} of {total} accepted
                </>
              )}
            </span>
            <Link to="/policies/tracking">Track acceptance →</Link>
          </p>
          {item.currentVersion > live.versionNumber && (
            <p className="policy-card-warning">
              {current} is not published — staff still see {liveLabel}.
            </p>
          )}
        </>
      ) : item.currentVersion === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
          Add content first, then publish it for staff to read and accept.
        </p>
      ) : (
        <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
          Not published for acceptance. Publishing asks every member to read and accept this exact
          version; you can track who has done so and export the evidence.
        </p>
      )}

      {list.length > 0 && (
        <ul className="policy-releases" aria-label="Release history">
          {list.map((r) => (
            <li key={r.id}>
              <strong>{versionLabel(r.documentVersion, r.versionNumber)}</strong>
              <Badge tone={r.requiresAcceptance ? 'primary' : 'neutral'}>
                {r.requiresAcceptance ? 'Material' : 'Minor'}
              </Badge>
              {r.supersededAt === null ? (
                <Badge tone="success">Live</Badge>
              ) : r.withdrawn ? (
                <Badge tone="warning">Withdrawn</Badge>
              ) : (
                <Badge tone="neutral">Superseded</Badge>
              )}
              <span className="policy-releases-meta">
                {formatDate(r.publishedAt)} by {r.publishedByName ?? 'unknown'} · {r.dueDays} days to accept
                {r.reacceptIntervalMonths ? ` · reconfirm every ${r.reacceptIntervalMonths} months` : ''} ·{' '}
                {r.acceptanceCount} {r.acceptanceCount === 1 ? 'acceptance' : 'acceptances'}
              </span>
              {r.releaseNotes && <span className="policy-releases-notes">{r.releaseNotes}</span>}
            </li>
          ))}
        </ul>
      )}

      {publishOpen && (
        <PublishPolicyDialog item={item} live={live} open={publishOpen} onClose={() => setPublishOpen(false)} />
      )}
      <Dialog open={withdrawOpen} onClose={() => setWithdrawOpen(false)} title="Withdraw from acceptance?">
        <p style={{ color: 'var(--color-text-muted)' }}>
          Staff will no longer be asked to accept “{item.title}”. Acceptances already given are kept
          as evidence, and the next publish will ask everyone to accept again.
        </p>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setWithdrawOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" loading={withdraw.isPending} onClick={() => withdraw.mutate()}>
            Withdraw
          </Button>
        </div>
      </Dialog>
    </Card>
  );
}
